import type { GenerationLayoutConfiguration } from '../../../../types/generationImpact';

export interface StudioLayoutHistoryEntry {
  label: string;
  before: GenerationLayoutConfiguration;
  after: GenerationLayoutConfiguration;
}

export interface StudioLayoutHistoryState {
  past: StudioLayoutHistoryEntry[];
  future: StudioLayoutHistoryEntry[];
}

export interface StudioHistoryStep {
  layout: GenerationLayoutConfiguration;
  label: string;
  state: StudioLayoutHistoryState;
}

export const EMPTY_STUDIO_LAYOUT_HISTORY: StudioLayoutHistoryState = {
  past: [],
  future: [],
};

function cloneLayout(layout: GenerationLayoutConfiguration): GenerationLayoutConfiguration {
  return structuredClone(layout);
}

export function recordStudioLayoutChange(
  state: StudioLayoutHistoryState,
  label: string,
  before: GenerationLayoutConfiguration,
  after: GenerationLayoutConfiguration,
  limit = 100,
): StudioLayoutHistoryState {
  if (JSON.stringify(before) === JSON.stringify(after)) return state;
  const entry: StudioLayoutHistoryEntry = {
    label,
    before: cloneLayout(before),
    after: cloneLayout(after),
  };
  return {
    past: [...state.past, entry].slice(-Math.max(1, limit)),
    future: [],
  };
}

export function undoStudioLayoutChange(
  state: StudioLayoutHistoryState,
): StudioHistoryStep | null {
  const entry = state.past.at(-1);
  if (!entry) return null;
  return {
    layout: cloneLayout(entry.before),
    label: entry.label,
    state: {
      past: state.past.slice(0, -1),
      future: [entry, ...state.future],
    },
  };
}

export function redoStudioLayoutChange(
  state: StudioLayoutHistoryState,
): StudioHistoryStep | null {
  const entry = state.future[0];
  if (!entry) return null;
  return {
    layout: cloneLayout(entry.after),
    label: entry.label,
    state: {
      past: [...state.past, entry],
      future: state.future.slice(1),
    },
  };
}
